import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch both projects and stats in parallel
      const [projectsRes, statsRes] = await Promise.all([
        fetch('/api/projects'),
        fetch('/api/projects/stats')
      ]);

      if (!projectsRes.ok || !statsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const projectsData = await projectsRes.json();
      const statsData = await statsRes.json();

      setProjects(projectsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects');
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message);
    }
  };

  const deleteProject = async (id) => {
    if (!confirm('Are you sure you want to delete this project?')) return;

    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete project');

      // Remove from UI
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project');
    }
  };

  const exportPDF = async (id, theme) => {
    try {
      console.log(`[PDF Export] Downloading PDF for project ${id}`);

      const response = await fetch(`/api/projects/${id}/export-pdf`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${theme.replace(/[^a-z0-9]/gi, '_')}-stickers.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`[PDF Export] PDF downloaded successfully`);
    } catch (err) {
      console.error('[PDF Export] Error:', err);
      alert(`Failed to export PDF: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Projects</h2>
        <div className="card">
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Projects</h2>
        <div className="card bg-red-50 border border-red-200">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-900">Your Projects</h2>
        <Link to="/new" className="btn-primary">
          + New Project
        </Link>
      </div>

      {/* Dashboard Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Projects</p>
                <p className="text-3xl font-bold text-indigo-600">{stats.totalProjects}</p>
              </div>
              <div className="text-4xl">📁</div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Stickers</p>
                <p className="text-3xl font-bold text-green-600">{stats.totalStickers}</p>
              </div>
              <div className="text-4xl">✨</div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Completed</p>
                <p className="text-3xl font-bold text-purple-600">{stats.completedProjects}</p>
              </div>
              <div className="text-4xl">🎉</div>
            </div>
          </div>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="card">
          <p className="text-gray-600 mb-4">
            No projects yet. Start creating your first sticker project!
          </p>
          <Link to="/new" className="btn-primary inline-block">
            Create First Project
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="card hover:shadow-lg transition-shadow overflow-hidden">
              {/* Sticker Preview Grid */}
              {project.preview_stickers && project.preview_stickers.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-50 p-3 rounded -m-4 mb-4">
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className="aspect-square bg-white rounded border-2 border-gray-200 overflow-hidden"
                    >
                      {project.preview_stickers[index] ? (
                        <img
                          src={`data:image/png;base64,${project.preview_stickers[index].image_data}`}
                          alt={`Sticker ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-2xl">
                          ?
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mb-4 bg-gray-50 p-8 rounded -m-4 mb-4 flex items-center justify-center">
                  <p className="text-gray-400 text-sm">No stickers yet</p>
                </div>
              )}

              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {project.theme}
                </h3>
                <span className={`text-xs px-2 py-1 rounded ${
                  project.status === 'completed' ? 'bg-green-100 text-green-800' :
                  project.status === 'generating' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {project.status}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <p>
                  <strong>Stickers:</strong> {project.sticker_count || 0}
                </p>
                <p>
                  <strong>Created:</strong> {new Date(project.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="flex gap-2">
                <Link
                  to={`/projects/${project.id}`}
                  className="btn-primary text-sm flex-1 text-center"
                >
                  👁️ View
                </Link>
                {project.sticker_count > 0 && (
                  <button
                    onClick={() => exportPDF(project.id, project.theme)}
                    className="btn-secondary text-sm px-3"
                    title="Export PDF"
                  >
                    📄
                  </button>
                )}
                <button
                  onClick={() => deleteProject(project.id)}
                  className="btn-secondary text-sm px-3 text-red-600 hover:bg-red-50"
                  title="Delete Project"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
